/**
 * Duplicate of ChangeHistoryTab for CandidateHistoryModal — profile ChangeHistoryTab is unchanged.
 */
import React from 'react';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { GridColumns } from '@mui/x-data-grid-pro';
import TimelineIcon from '@mui/icons-material/Timeline';
import { XGrid } from 'app/components/Common/XGrid/XGrid';
import { useDispatch, useSelector } from 'react-redux';
import { XGridDefaultColumn } from 'app/components/Common/XGrid/cells/XGridDefaults';
import { xGridDateSort } from 'app/components/Common/XGrid/sorting/XGridSorting';
import { XGridChangeHistoryOverflowCell } from 'app/components/Common/XGrid/cells/XGridChangeHistoryOverflowCell';
import { XGridHistoricalHoverView } from 'app/components/Common/XGrid/cells/HoverViewRenderers/XGridHistoricalHoverView';
import { selectChangeHistory } from 'app/components/Candidate/CandidateProfile/Profile/CandidateDetails.selector';
import { candidateDetailActions } from 'app/components/Candidate/CandidateProfile/Profile/CandidateDetails.redux';
import { XGridSSNClickableCell } from 'app/components/Candidate/CandidateProfile/CandidateTabPanel/ChangeHistoryTab/ChangeHistoryClickableCell';
import { useTranslation } from 'react-i18next';
import { CandidateHistoryModalChangeHistoryTimelineModal } from './CandidateHistoryModalChangeHistoryTimelineModal';
import type { IXGridBulkActionButton } from '@AMIEWEB/Common/XGrid/Toolbar/XGridToolbar';

const ZERO_GUID = '00000000-0000-0000-0000-000000000000';

const useStyles = makeStyles()({
  container: {
    padding: '24px 0px',
  },
});

type CandidateHistoryModalChangeHistoryTabProps = {
  candidateId: number;
  active: boolean;
};

export const CandidateHistoryModalChangeHistoryTab = ({
  candidateId,
  active,
}: CandidateHistoryModalChangeHistoryTabProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const changeHistory = useSelector(selectChangeHistory);
  const [timelineModalOpen, setTimelineModalOpen] = React.useState(false);

  const [sortModel, setSortModel] = React.useState<any>([
    {
      field: 'created',
      sort: 'desc',
    },
  ]);

  const rows = changeHistory?.rows || [];

  const fetchChangeHistory = React.useCallback(() => {
    dispatch(
      candidateDetailActions.getChangeHistory({
        candidateId,
        candidateGuid: ZERO_GUID,
      }),
    );
  }, [candidateId, dispatch]);

  React.useEffect(() => {
    if (active) {
      fetchChangeHistory();
    }
  }, [active, fetchChangeHistory]);

  const rightActions: IXGridBulkActionButton[] = React.useMemo(() => {
    if (!rows?.length) return [];
    return [
      {
        KeyId: 'timeline-visualization',
        Icon: TimelineIcon,
        tooltipProps: {
          tooltipContent: t('candidate.changeHistoryTab.timelineVisualization'),
          disabled: false,
        },
        onClick: () => setTimelineModalOpen(true),
      },
    ];
  }, [rows?.length, t]);

  const columns: GridColumns = [
    {
      ...XGridDefaultColumn,
      field: 'fieldChanged',
      headerName: 'Field',
    },
    {
      ...XGridDefaultColumn,
      field: 'oldValue',
      headerName: 'Old Value',
      renderCell: XGridChangeHistoryOverflowCell(XGridHistoricalHoverView)(XGridSSNClickableCell),
    },
    {
      ...XGridDefaultColumn,
      field: 'newValue',
      headerName: 'New Value',
      renderCell: XGridChangeHistoryOverflowCell(XGridHistoricalHoverView)(XGridSSNClickableCell),
    },
    {
      ...XGridDefaultColumn,
      field: 'userName',
      headerName: 'Changed By',
    },
    {
      ...XGridDefaultColumn,
      field: 'created',
      headerName: 'Date',
      type: 'date',
      sortComparator: xGridDateSort,
    },
    {
      ...XGridDefaultColumn,
      field: 'externalSystem',
      headerName: 'Source System',
    },
  ];

  const CandidateChangeHistoryGrid = React.useMemo(() => {
    return (
      <XGrid
        loading={changeHistory?.loading}
        columns={columns}
        rows={rows}
        headerHeight={45}
        autoHeight
        inDialog
        disableSelectionOnClick
        sortModel={sortModel}
        onSortModelChange={model => setSortModel(model)}
        toolbarProps={{
          refreshButton: true,
          refreshGrid: fetchChangeHistory,
          rightActions,
        }}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changeHistory?.rows, columns, rows, rightActions, fetchChangeHistory, changeHistory?.loading]);

  return (
    <Box className={classes.container}>
      {CandidateChangeHistoryGrid}
      <CandidateHistoryModalChangeHistoryTimelineModal
        open={timelineModalOpen}
        onClose={() => setTimelineModalOpen(false)}
        title={t('candidate.changeHistoryTab.timelineModalTitle')}
        historyData={rows}
      />
    </Box>
  );
};
